// Server-confirmed FT3D inventory move projection.
//
// This is deliberately not an optimistic model: the endpoint has already committed the move and
// reports the exact source/destination identities and quantities. We project that compact receipt
// into the browser's cached inventory trees. Table actions publish and install the shared browser
// delta; the receipt projection remains for callers outside that transport.

type MutableRecord = Record<string, unknown>;
type InventoryNode = MutableRecord;
type InventoryList = InventoryNode[];

export interface InventoryMoveReceiptSideSource {
    inventory_id: string;
    possess_id_before: string;
    item_qty_after: number;
    deleted_after: boolean;
}

export interface InventoryMoveReceiptSideDestination {
    inventory_id: string;
    possess_id: string;
    item_qty_after: number;
    created: boolean;
    /** Settlement may destroy the actual destination, including a freshly split stack. */
    deleted_after?: boolean;
    equipped: string;
    position: { x: number; y: number; z: number };
    /** Raw saved split shell, including fresh status identities and its base facet. */
    row?: MutableRecord;
}

export interface InventoryMoveReceipt {
    schema: 'ft3d.inventory-move-receipt.v1';
    ok: true;
    /** The shared browser truth installer already adopted this receipt's delta. */
    browser_applied?: true;
    member_revs?: Record<string, number>;
    mutation_id: string;
    move_id?: string;
    move: {
        outcome: 'full_move' | 'full_merge' | 'partial_split' | 'partial_merge';
        moved_qty: number;
        source: InventoryMoveReceiptSideSource;
        destination: InventoryMoveReceiptSideDestination;
        roots: { source: string | null; destination: string | null };
    };
    affected_caster_ids: string[];
    affected_encounter_ids: string[];
    timing: { accepted_unix_ms: number; commit_ms: number };
    /** MV5 (2026-10-02, N6d ask): the AP the move charged, attached by the page from the decision it posted (the endpoint charges exactly
     *  that after its AP CAS). Absent when the page posted no decision (PHP charged it itself). */
    ap_charged?: { cost: number; in_combat: boolean; before: number | null; after: number | null; source: 'page-decision' };
}

function asRecord(value: unknown): MutableRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as MutableRecord
        : null;
}

function scalar(value: unknown): unknown {
    return Array.isArray(value) ? value[0] : value;
}

function field(node: unknown, key: string): unknown {
    const record = asRecord(node);
    const bucket = asRecord(record?.inventory);
    return scalar(bucket && key in bucket ? bucket[key] : record?.[key]);
}

function inventoryId(node: unknown): string {
    return String(field(node, 'inventory_id') ?? '').toUpperCase();
}

function writeField(node: InventoryNode, key: string, value: unknown): void {
    let bucket = asRecord(node.inventory);
    if (!bucket) {
        bucket = {};
        node.inventory = bucket;
    }
    bucket[key] = value;
    if (key in node) node[key] = value;
}

function writePosition(node: InventoryNode, position: InventoryMoveReceiptSideDestination['position']): void {
    let bucket = asRecord(node.position);
    if (!bucket) {
        bucket = {};
        node.position = bucket;
    }
    bucket.x_pos = position.x;
    bucket.y_pos = position.y;
    bucket.z_pos = position.z;
}

function cloneNode(node: InventoryNode): InventoryNode {
    if (typeof structuredClone === 'function') return structuredClone(node) as InventoryNode;
    return JSON.parse(JSON.stringify(node)) as InventoryNode;
}

interface InventoryLocation {
    list: InventoryList;
    index: number;
    node: InventoryNode;
}

function visitInventoryLists(root: unknown, visitor: (list: InventoryList) => boolean | void): boolean {
    const seen = new Set<InventoryList>();
    const walk = (value: unknown): boolean => {
        if (!Array.isArray(value)) return false;
        const list = value.filter((row): row is InventoryNode => asRecord(row) !== null);
        // Use the actual array, not the filtered view, for mutations and indices.
        const actual = value as InventoryList;
        if (seen.has(actual)) return false;
        seen.add(actual);
        if (visitor(actual) === true) return true;
        for (const row of list) {
            if (walk(row.inventory_in_container)) return true;
        }
        return false;
    };

    const record = asRecord(root);
    if (!record) return false;
    if (walk(record.inventory)) return true;
    if (walk(record.inventory_in_container)) return true;
    if (walk(record.encounter_inventories_arr)) return true;
    const creatures = Array.isArray(record.encounter_creatures_arr) ? record.encounter_creatures_arr : [];
    for (const creature of creatures) {
        const creatureRecord = asRecord(creature);
        if (creatureRecord && walk(creatureRecord.inventory)) return true;
    }
    return false;
}

function findInventory(root: unknown, id: string): InventoryLocation | null {
    const wanted = id.toUpperCase();
    let found: InventoryLocation | null = null;
    visitInventoryLists(root, (list) => {
        const index = list.findIndex((node) => inventoryId(node) === wanted);
        if (index < 0) return false;
        found = { list, index, node: list[index]! };
        return true;
    });
    return found;
}

function rootId(root: MutableRecord): string {
    const encounterCreature = asRecord(root.encounter_creatures);
    const playerCharacter = asRecord(root.player_characters);
    const encounter = asRecord(root.encounters);
    const item = asRecord(root.items);
    return String(
        scalar(encounterCreature?.encounter_creature_id)
        ?? scalar(playerCharacter?.item_id)
        ?? scalar(encounter?.encounter_id)
        ?? scalar(item?.item_id)
        ?? '',
    ).toUpperCase();
}

function childListForPossessor(root: unknown, possessorId: string): InventoryList | null {
    const wanted = possessorId.toUpperCase();
    const record = asRecord(root);
    if (!record || !wanted) return null;

    if (rootId(record) === wanted) {
        if (String(scalar(asRecord(record.encounters)?.encounter_id) ?? '').toUpperCase() === wanted
            && Array.isArray(record.encounter_inventories_arr)) return record.encounter_inventories_arr as InventoryList;
        if (Array.isArray(record.inventory)) return record.inventory as InventoryList;
        if (Array.isArray(record.encounter_inventories_arr)) return record.encounter_inventories_arr as InventoryList;
    }

    const creatures = Array.isArray(record.encounter_creatures_arr) ? record.encounter_creatures_arr : [];
    for (const creature of creatures) {
        const creatureRecord = asRecord(creature);
        if (creatureRecord && rootId(creatureRecord) === wanted) {
            if (!Array.isArray(creatureRecord.inventory)) creatureRecord.inventory = [];
            return creatureRecord.inventory as InventoryList;
        }
    }

    const container = findInventory(root, wanted)?.node;
    if (!container) return null;
    if (!Array.isArray(container.inventory_in_container)) container.inventory_in_container = [];
    return container.inventory_in_container as InventoryList;
}

function patchDestinationNode(node: InventoryNode, receipt: InventoryMoveReceipt): void {
    const destination = receipt.move.destination;
    writeField(node, 'inventory_id', destination.inventory_id);
    writeField(node, 'possess_id', destination.possess_id);
    writeField(node, 'item_qty', destination.item_qty_after);
    writeField(node, 'inventory_deleted', 0);
    writeField(node, 'equipped', destination.equipped);
    writePosition(node, destination.position);
}

/** Apply a committed receipt to every distinct cached root/alias supplied by the caller. */
export function applyInventoryMoveReceiptToRoots(receipt: InventoryMoveReceipt, roots: unknown[]): number {
    const sourceId = receipt.move.source.inventory_id.toUpperCase();
    const destinationId = receipt.move.destination.inventory_id.toUpperCase();
    const uniqueRoots = Array.from(new Set(roots.filter((root) => asRecord(root) !== null)));
    let sourceTemplate: InventoryNode | null = null;
    for (const root of uniqueRoots) {
        const source = findInventory(root, sourceId)?.node;
        if (source) {
            sourceTemplate = cloneNode(source);
            break;
        }
    }
    const createdDestinationTemplate = asRecord(receipt.move.destination.row);

    let changedRoots = 0;
    for (const root of uniqueRoots) {
        let changed = false;
        const source = findInventory(root, sourceId);
        const destinationBefore = findInventory(root, destinationId);

        if (receipt.move.destination.deleted_after || receipt.move.destination.item_qty_after <= 0) {
            // A WS roster can precede this receipt. Retire every alias in the root, including
            // an old carrier snapshot beside the world row; findInventory returns only one.
            visitInventoryLists(root, (list) => {
                for (let index = list.length - 1; index >= 0; index--) {
                    const node = list[index]!;
                    const id = inventoryId(node);
                    if (id === destinationId) {
                        list.splice(index, 1);
                        changed = true;
                    } else if (id === sourceId) {
                        if (receipt.move.source.deleted_after || receipt.move.source.item_qty_after <= 0) {
                            list.splice(index, 1);
                        } else {
                            writeField(node, 'item_qty', receipt.move.source.item_qty_after);
                        }
                        changed = true;
                    }
                }
            });
            if (changed) changedRoots++;
            continue;
        }

        if (sourceId === destinationId) {
            const destinationList = childListForPossessor(root, receipt.move.destination.possess_id);
            if (source) {
                const movedNode = source.node;
                source.list.splice(source.index, 1);
                patchDestinationNode(movedNode, receipt);
                if (destinationList && !destinationList.some((node) => inventoryId(node) === destinationId)) {
                    destinationList.push(movedNode);
                }
                changed = true;
            } else if (destinationList && sourceTemplate
                && !destinationList.some((node) => inventoryId(node) === destinationId)) {
                const movedNode = cloneNode(sourceTemplate);
                patchDestinationNode(movedNode, receipt);
                destinationList.push(movedNode);
                changed = true;
            }
        } else {
            if (source) {
                if (receipt.move.source.deleted_after || receipt.move.source.item_qty_after <= 0) {
                    source.list.splice(source.index, 1);
                } else {
                    writeField(source.node, 'item_qty', receipt.move.source.item_qty_after);
                }
                changed = true;
            }

            const destination = findInventory(root, destinationId) ?? destinationBefore;
            if (destination) {
                patchDestinationNode(destination.node, receipt);
                changed = true;
            } else if (receipt.move.destination.created && (createdDestinationTemplate || sourceTemplate)) {
                const destinationList = childListForPossessor(root, receipt.move.destination.possess_id);
                if (destinationList) {
                    // Saved split identities win over the donor's identities. Table actions compile
                    // and install this arrival through the shared truth lane before this projection.
                    const created = cloneNode(createdDestinationTemplate ?? sourceTemplate!);
                    if (!createdDestinationTemplate) {
                        // Containers are deliberately non-stackable; a split row is an ordinary stack
                        // and must not duplicate malformed legacy child data from the source fallback.
                        created.inventory_in_container = [];
                    }
                    patchDestinationNode(created, receipt);
                    destinationList.push(created);
                    changed = true;
                }
            }
        }
        if (changed) changedRoots++;
    }
    return changedRoots;
}

/** Read-only assertion used by WS reconciliation telemetry and focused tests. */
export function inventoryReceiptMatchesRoot(root: unknown, receipt: InventoryMoveReceipt): boolean {
    const sourceId = receipt.move.source.inventory_id.toUpperCase();
    const destinationId = receipt.move.destination.inventory_id.toUpperCase();
    const destination = findInventory(root, destinationId)?.node;
    if (receipt.move.destination.deleted_after || receipt.move.destination.item_qty_after <= 0) {
        if (destination) return false;
        if (sourceId === destinationId) return true;
        const source = findInventory(root, sourceId)?.node;
        if (receipt.move.source.deleted_after || receipt.move.source.item_qty_after <= 0) return !source;
        return !!source && Number(field(source, 'item_qty')) === receipt.move.source.item_qty_after;
    }
    if (!destination
        || Number(field(destination, 'item_qty')) !== receipt.move.destination.item_qty_after
        || String(field(destination, 'possess_id') ?? '').toUpperCase() !== receipt.move.destination.possess_id.toUpperCase()) {
        return false;
    }
    if (sourceId === destinationId) return true;
    const source = findInventory(root, sourceId)?.node;
    if (receipt.move.source.deleted_after || receipt.move.source.item_qty_after <= 0) return !source;
    return !!source && Number(field(source, 'item_qty')) === receipt.move.source.item_qty_after;
}
